/**
 * Эквалайзер плеера: полосы, пресеты и расчёт запаса по громкости.
 *
 * <p><b>Десять полос по октавам (ISO 266).</b> 31 Гц … 16 кГц — та же сетка, что у
 * аппаратных графических эквалайзеров и у Poweramp/Neutron: человек, который когда-то крутил
 * такие ползунки, узнаёт их сразу. Крайние полосы — полки (`lowshelf`/`highshelf`), а не
 * колокола: «добавить баса» на слух значит поднять всё ниже 31 Гц, а не горб ровно на 31.
 * Средние — колокол шириной в октаву (Q = √2), чтобы соседние полосы смыкались без провалов.
 *
 * <p><b>Почему считаем характеристику сами.</b> Подъём на +6 дБ поднимает пик сигнала на те же
 * 6 дБ, и громкая запись, сведённая под 0 dBFS, начинает клиппировать — хрип, который люди
 * принимают за «плохой файл». Лечится предусилением: опустить весь сигнал ровно на столько,
 * на сколько поднимает самая высокая точка суммарной кривой. А она выше максимального ползунка:
 * соседние колокола складываются. Поэтому кривая считается по тем же формулам, по которым
 * считают сами фильтры (RBJ Audio EQ Cookbook — на них же построен BiquadFilterNode в Web Audio),
 * и из неё берётся настоящий максимум. Та же кривая рисуется на экране эквалайзера.
 *
 * <p><b>Ровный эквалайзер не трогает звук.</b> Когда все полосы в нуле или эквалайзер
 * выключен, движок вообще не включает фильтры в цепочку (см. {@link isEqualizerTransparent}):
 * сигнал идёт от файла к выходу без единой лишней операции.
 */

export const EQ_BAND_FREQUENCIES = [31, 62, 125, 250, 500, 1000, 2000, 4000, 8000, 16000] as const;

/** Предел ползунка. Больше ±12 дБ даёт уже не коррекцию, а искажение и потерю запаса. */
export const EQ_MAX_GAIN_DB = 12;

/** Ширина колокола в одну октаву. */
export const EQ_PEAKING_Q = Math.SQRT2;

export type EqFilterType = 'lowshelf' | 'peaking' | 'highshelf';

export type EqPresetId =
  | 'flat'
  | 'bass'
  | 'treble'
  | 'vocal'
  | 'rock'
  | 'pop'
  | 'jazz'
  | 'classical'
  | 'electronic'
  | 'punk'
  | 'rave'
  | 'night'
  | 'custom';

export type EqualizerState = {
  enabled: boolean;
  preset: EqPresetId;
  /** По одному значению на полосу, дБ. */
  gains: number[];
};

const FLAT = EQ_BAND_FREQUENCIES.map(() => 0);

/**
 * Пресеты. Значения — умеренные (до +6 дБ): пресет должен звучать «по-другому», а не «громче»,
 * — громкость всё равно выравнивается предусилением, и крутой пресет лишь съедает динамику.
 */
export const EQ_PRESETS: Record<Exclude<EqPresetId, 'custom'>, readonly number[]> = {
  flat: FLAT,
  bass: [6, 5, 4, 2, 0, 0, 0, 0, 0, 0],
  treble: [0, 0, 0, 0, 0, 1, 2, 4, 5, 6],
  vocal: [-2, -2, -1, 1, 3, 4, 3, 1, 0, -1],
  rock: [4, 3, 2, 0, -1, -1, 1, 2, 3, 4],
  pop: [-1, 0, 2, 3, 4, 3, 1, 0, -1, -1],
  jazz: [3, 2, 1, 2, -1, -1, 0, 1, 2, 3],
  classical: [4, 3, 2, 1, -1, -1, 0, 2, 3, 4],
  electronic: [5, 4, 1, 0, -2, 1, 0, 1, 4, 5],
  // Панк-хардкор (заказ 2026-10-05): плотная бочка на 62–125 Гц, вынутая «каша» 250–500 Гц,
  // где гитары с басом слипаются, и выдвинутый перегруз гитар с криком на 2–4 кГц. Самый верх
  // не задираем — на быстрых тарелках он режет уши.
  punk: [2, 4, 3, -2, -3, 0, 3, 5, 3, 0],
  // Рейв: саб и бочка вперёд, середина отступает, наверху блеск хэтов и синтов. Глубже
  // «Электроники» в самом низу — под большой звук, а не под фон.
  rave: [6, 6, 3, 0, -2, -2, 0, 2, 4, 5],
  // Тихое прослушивание: на малой громкости ухо хуже слышит края диапазона (кривые равной
  // громкости), и музыка ночью звучит плоско. Поднимаем бас и верх — как кнопка Loudness.
  night: [5, 3, 1, 0, 0, 0, 0, 1, 3, 4],
};

export const EQ_PRESET_ORDER: readonly Exclude<EqPresetId, 'custom'>[] = [
  'flat',
  'bass',
  'treble',
  'vocal',
  'rock',
  'pop',
  'jazz',
  'classical',
  'electronic',
  'punk',
  'rave',
  'night',
];

export const DEFAULT_EQUALIZER: EqualizerState = { enabled: false, preset: 'flat', gains: [...FLAT] };

export function filterTypeForBand(index: number): EqFilterType {
  if (index === 0) {
    return 'lowshelf';
  }
  if (index === EQ_BAND_FREQUENCIES.length - 1) {
    return 'highshelf';
  }
  return 'peaking';
}

export function clampGain(db: number): number {
  if (!Number.isFinite(db)) {
    return 0;
  }
  // Шаг 0.5 дБ: мельче на слух не различить, а «+3.1734» на подписи выглядит как сбой.
  const rounded = Math.round(db * 2) / 2;
  return Math.max(-EQ_MAX_GAIN_DB, Math.min(EQ_MAX_GAIN_DB, rounded));
}

/** Восстановить состояние из хранилища: всё, что не похоже на десять чисел, — ровная линия. */
export function normalizeEqualizer(raw: unknown): EqualizerState {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_EQUALIZER, gains: [...FLAT] };
  }
  const r = raw as Partial<EqualizerState>;
  const gains =
    Array.isArray(r.gains) && r.gains.length === EQ_BAND_FREQUENCIES.length
      ? r.gains.map((g) => clampGain(Number(g)))
      : [...FLAT];
  const preset: EqPresetId =
    typeof r.preset === 'string' && (r.preset === 'custom' || r.preset in EQ_PRESETS)
      ? (r.preset as EqPresetId)
      : 'custom';
  return { enabled: r.enabled === true, preset, gains };
}

export function applyPreset(state: EqualizerState, preset: Exclude<EqPresetId, 'custom'>): EqualizerState {
  return { enabled: true, preset, gains: [...EQ_PRESETS[preset]] };
}

/** Ползунок тронут вручную: пресет превращается в «свой», если значения от него ушли. */
export function setBandGain(state: EqualizerState, band: number, db: number): EqualizerState {
  const gains = state.gains.map((g, i) => (i === band ? clampGain(db) : g));
  return { enabled: true, preset: matchPreset(gains), gains };
}

export function matchPreset(gains: readonly number[]): EqPresetId {
  for (const id of EQ_PRESET_ORDER) {
    const p = EQ_PRESETS[id];
    if (p.every((g, i) => g === gains[i])) {
      return id;
    }
  }
  return 'custom';
}

export function isEqualizerTransparent(state: EqualizerState): boolean {
  return !state.enabled || state.gains.every((g) => g === 0);
}

type Coefficients = { b0: number; b1: number; b2: number; a0: number; a1: number; a2: number };

/**
 * Коэффициенты биквада по RBJ Cookbook в той же редакции, что и спецификация Web Audio:
 * у полок крутизна S = 1, поэтому Q на них не влияет.
 */
export function biquadCoefficients(
  type: EqFilterType,
  frequency: number,
  gainDb: number,
  sampleRate: number,
  q: number = EQ_PEAKING_Q,
): Coefficients {
  const A = Math.pow(10, gainDb / 40);
  const w0 = (2 * Math.PI * Math.min(frequency, sampleRate / 2 - 1)) / sampleRate;
  const cos = Math.cos(w0);
  const sin = Math.sin(w0);
  if (type === 'peaking') {
    const alpha = sin / (2 * q);
    return {
      b0: 1 + alpha * A,
      b1: -2 * cos,
      b2: 1 - alpha * A,
      a0: 1 + alpha / A,
      a1: -2 * cos,
      a2: 1 - alpha / A,
    };
  }
  // S = 1 → alpha = sin/2 · √2
  const alpha = (sin / 2) * Math.SQRT2;
  const sqA2alpha = 2 * Math.sqrt(A) * alpha;
  if (type === 'lowshelf') {
    return {
      b0: A * (A + 1 - (A - 1) * cos + sqA2alpha),
      b1: 2 * A * (A - 1 - (A + 1) * cos),
      b2: A * (A + 1 - (A - 1) * cos - sqA2alpha),
      a0: A + 1 + (A - 1) * cos + sqA2alpha,
      a1: -2 * (A - 1 + (A + 1) * cos),
      a2: A + 1 + (A - 1) * cos - sqA2alpha,
    };
  }
  return {
    b0: A * (A + 1 + (A - 1) * cos + sqA2alpha),
    b1: -2 * A * (A - 1 + (A + 1) * cos),
    b2: A * (A + 1 + (A - 1) * cos - sqA2alpha),
    a0: A + 1 - (A - 1) * cos + sqA2alpha,
    a1: 2 * (A - 1 - (A + 1) * cos),
    a2: A + 1 - (A - 1) * cos - sqA2alpha,
  };
}

/** |H(e^jw)| в децибелах. */
function magnitudeDb(c: Coefficients, frequency: number, sampleRate: number): number {
  const w = (2 * Math.PI * frequency) / sampleRate;
  const cos1 = Math.cos(w);
  const sin1 = Math.sin(w);
  const cos2 = Math.cos(2 * w);
  const sin2 = Math.sin(2 * w);
  const numRe = c.b0 + c.b1 * cos1 + c.b2 * cos2;
  const numIm = -(c.b1 * sin1 + c.b2 * sin2);
  const denRe = c.a0 + c.a1 * cos1 + c.a2 * cos2;
  const denIm = -(c.a1 * sin1 + c.a2 * sin2);
  const num = numRe * numRe + numIm * numIm;
  const den = denRe * denRe + denIm * denIm;
  return 10 * Math.log10(num / den);
}

/** Частоты на логарифмической шкале 20 Гц — 20 кГц: так их слышит ухо и так рисуется кривая. */
export function logFrequencies(points: number): number[] {
  const lo = Math.log10(20);
  const hi = Math.log10(20000);
  return Array.from({ length: points }, (_, i) => Math.pow(10, lo + ((hi - lo) * i) / (points - 1)));
}

/** Суммарная АЧХ всех полос в указанных точках, дБ. */
export function responseCurve(
  gains: readonly number[],
  frequencies: readonly number[],
  sampleRate = 48000,
): number[] {
  const filters = gains.map((g, i) =>
    biquadCoefficients(filterTypeForBand(i), EQ_BAND_FREQUENCIES[i], g, sampleRate),
  );
  return frequencies.map((f) => filters.reduce((sum, c) => sum + magnitudeDb(c, f, sampleRate), 0));
}

/**
 * Предусиление, дБ (≤ 0): ровно на столько опустить сигнал, чтобы самая высокая точка
 * кривой эквалайзера пришлась на 0 дБ. Сетка частот — плотная плюс центры полос: максимум
 * колокола приходится как раз на центр, и между точками сетки его можно было бы пропустить.
 */
export function equalizerPreampDb(state: EqualizerState, sampleRate = 48000): number {
  if (isEqualizerTransparent(state)) {
    return 0;
  }
  const grid = [...logFrequencies(160), ...EQ_BAND_FREQUENCIES];
  const peak = Math.max(0, ...responseCurve(state.gains, grid, sampleRate));
  // `0 - x`, а не `-x`: у ровной кривой иначе получается -0, и сравнения с нулём врут.
  return 0 - Math.round(peak * 10) / 10;
}

/**
 * Выравнивание громкости по ReplayGain. Подъём тихого трека ограничен его пиком: поднимать
 * дальше 0 dBFS — тот же клиппинг, от которого спасает предусиление.
 */
export function replayGainDb(gainDb: number | undefined, peak: number | undefined): number {
  if (gainDb === undefined || !Number.isFinite(gainDb)) {
    return 0;
  }
  if (peak !== undefined && Number.isFinite(peak) && peak > 0) {
    const headroom = -20 * Math.log10(peak);
    return Math.min(gainDb, headroom);
  }
  return Math.min(gainDb, 0);
}

export function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}
